import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const LANGS = [
  { code: "en", label: "English", short: "EN", icon: "🇬🇧" },
  { code: "fr", label: "Français", short: "FR", icon: "🇫🇷" },
];

/**
 * Language switcher (English / Français). Persists the choice to localStorage
 * via i18next and opens on the visitor's browser language by default.
 */
export default function LanguageSwitcher({
  scrolled = false,
  align = "right",
}: {
  scrolled?: boolean;
  align?: "left" | "right";
}) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current =
    LANGS.find((l) => l.code === i18n.language) ??
    LANGS.find((l) => i18n.language?.startsWith(l.code)) ??
    LANGS[0];

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const buttonBase = scrolled
    ? "text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50"
    : "text-white/90 hover:text-white hover:bg-white/10";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-2.5 py-2 text-xs font-semibold rounded-md transition-colors whitespace-nowrap cursor-pointer ${buttonBase}`}
        aria-label="Change language"
      >
        <span className="text-sm leading-none">{current.icon}</span>
        <span>{current.short}</span>
        <i className="ri-arrow-down-s-line text-xs" />
      </button>

      {open && (
        <div
          className={`absolute top-full mt-1 w-40 bg-white rounded-lg border border-background-200 shadow-lg py-1 z-50 ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => {
                void i18n.changeLanguage(l.code);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left transition-colors cursor-pointer ${
                current.code === l.code
                  ? "text-accent-600 bg-accent-50 font-semibold"
                  : "text-foreground-700 hover:bg-background-50"
              }`}
            >
              <span>{l.icon}</span>
              <span>{l.label}</span>
              {current.code === l.code && (
                <i className="ri-check-line text-sm ml-auto" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}